import type {ReactAction} from '@netx/core/bootstrap';
import {actionsToOrderedActions} from './actionsToOrderedActions';

export type ParseActionsCommonOptions = {
	sort?: boolean;
	order?: boolean;
	omit?: string[] | ((action: ReactAction) => boolean);
	pick?: string[] | ((action: ReactAction) => boolean);
};

const shouldOmit = (
	action: ReactAction,
	omit?: string[] | ((action: ReactAction) => boolean),
) => {
	if (!omit) return false;
	return typeof omit === 'function' ? omit(action) : omit.includes(action.name);
};

const shouldPick = (
	action: ReactAction,
	pick?: string[] | ((action: ReactAction) => boolean),
) => {
	if (!pick) return true;
	return typeof pick === 'function' ? pick(action) : pick.includes(action.name);
};

export interface ParseActionsParams extends ParseActionsCommonOptions {
	actions: ReactAction[];
	args?: any[];
}

export const parseActions = ({
	actions,
	args,
	order = true,
	omit,
	pick,
	sort = true,
}: ParseActionsParams): Array<ReactAction | ReactAction[]> => {
	const filtered = actions.filter((action) => {
		if (shouldOmit(action, omit)) return false;
		if (!shouldPick(action, pick)) return false;
		if (
			action.enableTest &&
			action.enableTest.apply(action.enableTest, args ?? []) === false
		)
			return false;
		return true;
	});

	const parsed = filtered.reduce<ReactAction[]>((acc, action) => {
		if (action.children?.length) {
			const children = parseActions({
				actions: action.children as ReactAction[],
				args,
				order,
				omit,
				pick,
			});
			return !children.flat().length
				? acc
				: [
						...acc,
						{
							...action,
							children: children as any,
						},
					];
		}
		return [...acc, action];
	}, []);

	return order ? actionsToOrderedActions(parsed, sort === false) : parsed;
};
